import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '@/app/store'
import { csFlowApi } from './csFlowAPI'
import apiService from '../../services/api'

// CS Cart State Interface - matches Flutter's CsCartController
interface CsCartState {
  // Reactive cart items list
  cartItems: Array<Record<string, any>>
  cartInfo: Record<string, any> | null
  isLoading: boolean
  isAddingItem: boolean
  isClearingCart: boolean
  isUpdatingPrices: boolean
  isDeletingItem: boolean
  deletingItemId: string | null
  error: string | null
  isInitialized: boolean
  isLoadingCart: boolean
  justAddedItem: boolean
}

const initialCsCartState: CsCartState = {
  cartItems: [],
  cartInfo: null,
  isLoading: false,
  isAddingItem: false,
  isClearingCart: false,
  isUpdatingPrices: false,
  isDeletingItem: false,
  deletingItemId: null,
  error: null,
  isInitialized: false,
  isLoadingCart: false,
  justAddedItem: false,
}

// Helper functions - same as cashier cart
function safeString(value: any, fallback: string): string {
  try {
    if (value == null) return fallback
    const str = value.toString().trim()
    if (str.length === 0 || str === 'null' || str === 'undefined') return fallback
    return str
  } catch (e) {
    return fallback
  }
}

function safeQuantity(value: any): string {
  try {
    if (value == null) return '1'
    const quantity = parseInt(value.toString()) || 1
    return (quantity < 1 ? 1 : quantity).toString()
  } catch (e) {
    return '1'
  }
}

function safePrice(value: any): string {
  try {
    if (value == null) return '0'
    const price = parseFloat(value.toString()) || 0.0
    return (price < 0 ? 0.0 : price).toString()
  } catch (e) {
    return '0'
  }
}

function isCsCartMissingError(error: any): boolean {
  try {
    const status = error?.response?.status
    if (status === 404 || status === 410) {
      return true
    }

    const message = (
      error?.response?.data?.descriptiveMessage ??
      error?.response?.data?.message ??
      error?.response?.data?.error ??
      error?.message ??
      ''
    )
      .toString()
      .toLowerCase()

    if (message.includes('السلة غير موجودة') || message.includes('cart not found')) {
      return true
    }
  } catch (e) {
    return false
  }
  return false
}

// Helper function to process raw cart item - same logic as cashier cart
function processRawCartItem(rawItem: any): Record<string, any> | null {
  try {
    if (!rawItem || typeof rawItem !== 'object') {
      return null
    }

    const cartItem = { ...rawItem }

    // Ensure critical fields are never null
    cartItem.id = safeString(cartItem.id, '')
    cartItem.cart_id = safeString(cartItem.cart_id, '')
    cartItem.quantity = safeQuantity(cartItem.quantity)
    cartItem.unit_price = safePrice(cartItem.unit_price)
    cartItem.total_unit_price = safePrice(cartItem.total_unit_price)

    // Extract name and image from meals array with safety - same as cashier cart
    let itemName = 'Unknown Item'
    let imageUrl = ''
    let description = ''
    let calories = ''
    let size = ''
    let productName = ''
    let additionals: any[] = []

    try {
      if (cartItem.meals && Array.isArray(cartItem.meals)) {
        const meals = cartItem.meals
        if (meals.length > 0 && meals[0] != null) {
          const meal = meals[0]

          // Extract product name first (prioritize product name over meal name)
          if (meal.product && typeof meal.product === 'object') {
            const product = meal.product
            productName = safeString(product.name, '')
            if (productName.length > 0 && productName !== 'null') {
              itemName = productName // Use product name as primary name
            } else {
              itemName = safeString(meal.name, 'Unknown Item')
            }
          } else {
            itemName = safeString(meal.name, 'Unknown Item')
          }

          imageUrl = safeString(meal.image, '')
          description = safeString(meal.description, '')
          calories = safeString(meal.calories, '')
          size = safeString(meal.size, '')

          // Safe additionals extraction - preserve existing additionals if available
          if (meal.additionals && Array.isArray(meal.additionals)) {
            additionals = meal.additionals
          } else if (cartItem.additionals && Array.isArray(cartItem.additionals)) {
            // Fallback to existing additionals in cart item
            additionals = cartItem.additionals
          } else if (cartItem.extras && Array.isArray(cartItem.extras)) {
            // Additional fallback to extras field
            additionals = cartItem.extras
          } else if (cartItem.addons && Array.isArray(cartItem.addons)) {
            // Additional fallback to addons field
            additionals = cartItem.addons
          }
        }
      }
    } catch (e) {
      // Continue with defaults
    }

    // Set extracted values
    cartItem.name = itemName
    cartItem.image_url = imageUrl
    cartItem.image = imageUrl // Also set image for backward compatibility
    cartItem.description = description
    cartItem.calories = calories
    cartItem.size = size
    cartItem.product_name = productName
    cartItem.additionals = additionals

    // Ensure extras field exists
    if (!cartItem.extras || !Array.isArray(cartItem.extras)) {
      cartItem.extras = []
    }

    // Keep price for backward compatibility
    cartItem.price = cartItem.unit_price

    // Calculate total_price if not present
    if (!cartItem.total_price ||
      cartItem.total_price.toString().length === 0 ||
      cartItem.total_price.toString() === '0') {
      const price = parseFloat(cartItem.unit_price?.toString() ?? '0') || 0.0
      const quantity = parseInt(cartItem.quantity?.toString() ?? '1') || 1
      cartItem.total_price = (price * quantity).toString()
    }

    // Preserve meals array for CartItemCard to access
    if (!cartItem.meals || !Array.isArray(cartItem.meals)) {
      cartItem.meals = []
    }

    // Preserve other fields
    cartItem.notes = cartItem.notes || ''
    cartItem.product_id = cartItem.product_id?.toString() ?? cartItem.item_id?.toString() ?? ''
    cartItem.meal_id = cartItem.meal_id?.toString() ?? ''

    return cartItem
  } catch (e) {
    return null
  }
}

// Async thunk to load CS cart items
export const loadCsCartItems = createAsyncThunk(
  'csCart/loadCsCartItems',
  async (params: { customerId: number; forceRefresh?: boolean; couponCode?: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState

      // Check if customer is selected
      if (!params.customerId || params.customerId === 0) {
        return { cartItems: [], cartInfo: null }
      }

      // An explicit empty couponCode means "no coupon" and must not fall back to Redux
      const resolvedCouponCode = params.couponCode !== undefined
        ? params.couponCode.trim()
        : (state.discount?.discountCode?.trim() || '')

      // Protection: if last update was less than 1 second ago and not force refresh, ignore call
      // For force refresh, always fetch latest data
      if (!params.forceRefresh) {
        const lastUpdated = state.csCart.cartInfo?.last_updated
        if (lastUpdated) {
          try {
            const lastUpdateTime = new Date(lastUpdated)
            const timeSinceLastUpdate = Date.now() - lastUpdateTime.getTime()
            if (timeSinceLastUpdate < 1000) {
              return {
                cartItems: state.csCart.cartItems,
                cartInfo: state.csCart.cartInfo
              }
            }
          } catch (e) {
            // Ignore date parsing error
          }
        }
      }

      // Always fetch fresh data when forceRefresh is true
      // Use direct API call to ensure it's executed immediately
      let responseData: any = null

      const buildUrlWithCoupon = (coupon?: string) => {
        let url = `/customerService/carts/user/${params.customerId}`
        if (coupon && coupon.length > 0) {
          const query = new URLSearchParams()
          query.append('coboneCode', coupon)
          url += `?${query.toString()}`
        }
        return url
      }

      if (params.forceRefresh) {
        // Use direct API call for force refresh to ensure immediate execution
        const response = await apiService.axios.get(buildUrlWithCoupon(resolvedCouponCode))
        responseData = response.data
      } else {
        // Use RTK Query for normal refresh (with cache)
        const response = await csFlowApi.endpoints.getCsCartData.initiate(
          { customerId: params.customerId, couponCode: resolvedCouponCode },
          {
            forceRefetch: false,
          }
        )

        if ('data' in response && response.data) {
          responseData = response.data as any
        }
      }

      if (responseData) {
        const dataField = responseData.data || responseData

        // Update cart info
        const cartInfo: Record<string, any> = {
          id: dataField.id?.toString() ?? '',
          user_id: dataField.user_id?.toString() ?? '',
          branch_id: dataField.branch_id?.toString() ?? '',
          type_id: dataField.type_id?.toString() ?? '',
          address_id: dataField.address_id?.toString() ?? '',
          table_id: dataField.table_id?.toString() ?? '',
          tax_percentage: dataField.tax_percentage?.toString() ?? '15',
          delivery_price: dataField.delivery_price?.toString() ?? '0',
          table_price: dataField.table_price?.toString() ?? '0',
          service_price: dataField.service_price?.toString() ?? '0',
          sub_total: dataField.sub_total?.toString() ?? '0',
          tax_value: dataField.tax_value?.toString() ?? '0',
          total_price: dataField.total_price?.toString() ?? '0',
          discount: dataField.discount?.toString() ?? '0',
          coupon_code: dataField.coupon_code?.toString() ?? dataField.coboneCode?.toString() ?? '',
          last_updated: new Date().toISOString(),
        }

        // Save cart ID to localStorage
        if (cartInfo.id) {
          localStorage.setItem('cs_cart_id', cartInfo.id)
        }

        // Update cart items
        let cartItems: Array<Record<string, any>> = []
        if (dataField.items && Array.isArray(dataField.items)) {
          cartItems = dataField.items
            .filter((item: any) => item != null)
            .map((item: any) => processRawCartItem(item))
            .filter((item: Record<string, any> | null) =>
              item != null &&
              Object.keys(item).length > 0 &&
              item.id?.toString().length > 0
            ) as Array<Record<string, any>>
        }

        return { cartItems, cartInfo }
      }

      return { cartItems: [], cartInfo: null }
    } catch (error: any) {
      if (isCsCartMissingError(error)) {
        localStorage.removeItem('cs_cart_id')
        return { cartItems: [], cartInfo: null }
      }
      return rejectWithValue(error.message || 'Failed to load cart')
    }
  }
)

// Async thunk to refresh CS cart items
export const refreshCsCartItems = createAsyncThunk(
  'csCart/refreshCsCartItems',
  async (params: { customerId: number; forceRefresh?: boolean; couponCode?: string }, { dispatch }) => {
    return await dispatch(loadCsCartItems(params)).unwrap()
  }
)

const csCartSlice = createSlice({
  name: 'csCart',
  initialState: initialCsCartState,
  reducers: {
    // Mark that a new item was just added
    markItemAdded: (state) => {
      state.justAddedItem = true
    },

    // Set adding item state
    setAddingItem: (state, action: PayloadAction<boolean>) => {
      state.isAddingItem = action.payload
    },

    // Set clearing cart state
    setClearingCart: (state, action: PayloadAction<boolean>) => {
      state.isClearingCart = action.payload
    },

    // Set updating prices state
    setUpdatingPrices: (state, action: PayloadAction<boolean>) => {
      state.isUpdatingPrices = action.payload
    },

    // Set deleting item state
    setDeletingItem: (state, action: PayloadAction<boolean>) => {
      state.isDeletingItem = action.payload
      if (!action.payload) {
        state.deletingItemId = null
      }
    },
    
    // Set deleting item ID
    setDeletingItemId: (state, action: PayloadAction<string | null>) => {
      state.deletingItemId = action.payload
      state.isDeletingItem = action.payload !== null
    },

    // Update cart data from API response
    updateCartDataFromApi: (state, action: PayloadAction<Record<string, any>>) => {
      const cartData = action.payload

      // Update cart info
      state.cartInfo = cartData

      // Update cart items
      if (cartData.items && Array.isArray(cartData.items)) {
        state.cartItems = cartData.items
          .filter((item: any) => item != null)
          .map((item: any) => processRawCartItem(item))
          .filter((item: Record<string, any> | null) =>
            item != null &&
            Object.keys(item).length > 0 &&
            item.id?.toString().length > 0
          ) as Array<Record<string, any>>
      } else {
        state.cartItems = []
      }
    },

    // Reset cart state
    resetCart: (state) => {
      state.cartItems = []
      state.cartInfo = null
      state.isLoading = false
      state.isClearingCart = false
      state.isDeletingItem = false
      state.deletingItemId = null
      state.error = null
      state.justAddedItem = false
      localStorage.removeItem('cs_cart_id')
    },

    // Clear cart locally
    clearCartLocally: (state) => {
      state.cartItems = []
      state.cartInfo = {
        sub_total: '0',
        tax_value: '0',
        total_price: '0',
        discount: '0',
        items_count: '0',
        tax_percentage: '15',
        delivery_price: '0',
        table_price: '0',
        type_id: '0',
      }
      state.isClearingCart = true
      state.isDeletingItem = false
      state.deletingItemId = null
      state.error = null
      state.justAddedItem = false
      // Clear localStorage cart ID
      localStorage.removeItem('cs_cart_id')
    },
  },
  extraReducers: (builder) => {
    builder
      // Load cart items
      .addCase(loadCsCartItems.pending, (state) => {
        state.isLoadingCart = true
        if (!state.isInitialized) {
          state.isLoading = true
        }
        state.error = null
      })
      .addCase(loadCsCartItems.fulfilled, (state, action) => {
        state.isLoadingCart = false
        state.isLoading = false
        state.isInitialized = true
        state.cartItems = action.payload.cartItems
        state.cartInfo = action.payload.cartInfo
        state.error = null
        state.justAddedItem = false
      })
      .addCase(loadCsCartItems.rejected, (state, action) => {
        state.isLoadingCart = false
        state.isLoading = false
        state.error = action.payload as string || 'Failed to load cart'
      })

      // Refresh cart items
      .addCase(refreshCsCartItems.pending, (state) => {
        if (!state.isClearingCart) {
          state.isLoadingCart = true
        }
      })
      .addCase(refreshCsCartItems.fulfilled, (state, action) => {
        state.isLoadingCart = false
        state.isUpdatingPrices = false
        state.isDeletingItem = false
        state.deletingItemId = null
        state.cartItems = action.payload.cartItems
        state.cartInfo = action.payload.cartInfo
        state.justAddedItem = false
      })
      .addCase(refreshCsCartItems.rejected, (state) => {
        state.isLoadingCart = false
        state.isUpdatingPrices = false
        state.isDeletingItem = false
        state.deletingItemId = null
      })
  },
})

// Selectors
export const selectCsCartItems = (state: RootState) => state.csCart.cartItems
export const selectCsCartInfo = (state: RootState) => state.csCart.cartInfo
export const selectIsCsCartEmpty = (state: RootState) => state.csCart.cartItems.length === 0
export const selectCsCartId = (state: RootState) => state.csCart.cartInfo?.id?.toString() || ''

// Cart totals - Use API values directly
export const selectCsCartSubTotal = (state: RootState): number => {
  try {
    return parseFloat(state.csCart.cartInfo?.sub_total?.toString() ?? '0') || 0.0
  } catch (e) {
    return 0.0
  }
}

export const selectCsCartTaxValue = (state: RootState): number => {
  try {
    return parseFloat(state.csCart.cartInfo?.tax_value?.toString() ?? '0') || 0.0
  } catch (e) {
    return 0.0
  }
}

export const selectCsCartTotalPrice = (state: RootState): number => {
  try {
    return parseFloat(state.csCart.cartInfo?.total_price?.toString() ?? '0') || 0.0
  } catch (e) {
    return 0.0
  }
}

export const selectCsCartDeliveryPrice = (state: RootState): number => {
  try {
    return parseFloat(state.csCart.cartInfo?.delivery_price?.toString() ?? '0') || 0.0
  } catch (e) {
    return 0.0
  }
}

export const selectCsCartTablePrice = (state: RootState): number => {
  try {
    return parseFloat(state.csCart.cartInfo?.table_price?.toString() ?? '0') || 0.0
  } catch (e) {
    return 0.0
  }
}

export const selectCsCartDiscountAmount = (state: RootState): number => {
  try {
    return parseFloat(state.csCart.cartInfo?.discount?.toString() ?? '0') || 0.0
  } catch (e) {
    return 0.0
  }
}

// Loading state selectors
export const selectIsCsCartLoading = (state: RootState) => state.csCart.isLoading
export const selectIsCsCartLoadingCart = (state: RootState) => state.csCart.isLoadingCart
export const selectIsCsCartUpdatingPrices = (state: RootState) => state.csCart.isUpdatingPrices
export const selectIsCsCartAddingItem = (state: RootState) => state.csCart.isAddingItem
export const selectIsCsCartDeletingItem = (state: RootState) => state.csCart.isDeletingItem
export const selectCsCartDeletingItemId = (state: RootState) => state.csCart.deletingItemId

export const {
  markItemAdded,
  setAddingItem,
  setClearingCart,
  setUpdatingPrices,
  setDeletingItem,
  setDeletingItemId,
  updateCartDataFromApi,
  resetCart,
  clearCartLocally,
} = csCartSlice.actions

export default csCartSlice.reducer





